import React from 'react';
import { motion } from 'motion/react';

interface LogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  showSlogan?: boolean;
}

export const Logo: React.FC<LogoProps> = ({ 
  className = "", 
  size = 'md',
  showText = true,
  showSlogan = false
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  };

  const textClasses = {
    sm: 'text-base',
    md: 'text-xl',
    lg: 'text-3xl',
    xl: 'text-5xl'
  };

  const sloganClasses = {
    sm: 'text-[8px]',
    md: 'text-[10px]',
    lg: 'text-xs',
    xl: 'text-sm'
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <motion.div 
        className={`${sizeClasses[size]} relative flex-shrink-0`}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
        transition={{ 
          scale: { type: "spring", stiffness: 300, damping: 15 },
          rotate: { duration: 0.5, ease: "easeInOut" }
        }}
      >
        {/* Background Glow */}
        <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-full" />
        
        <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-md">
          {/* Main Shape: A hybrid of a Location Pin and a "Sent" Arrow */}
          <defs>
            <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#16a34a" /> {/* green-600 */}
              <stop offset="100%" stopColor="#15803d" /> {/* green-700 */}
            </linearGradient>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <blur stdDeviation="2" />
            </filter>
          </defs>

          {/* Outer Pin Body */}
          <path 
            d="M50 95C50 95 85 65 85 40C85 20.67 69.33 5 50 5C30.67 5 15 20.67 15 40C15 65 50 95 50 95Z" 
            fill="url(#logoGradient)"
          />
          
          {/* Inner "Sent" Paper Plane Element */}
          <motion.path 
            d="M40 35L65 47.5L40 60L46 47.5L40 35Z" 
            fill="white"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          />
          
          {/* Communication Wave Dots */}
          <circle cx="50" cy="15" r="2" fill="white" opacity="0.6">
            <animate attributeName="opacity" values="0.2;0.6;0.2" dur="2s" repeatCount="indefinite" />
          </circle>
          <circle cx="35" cy="22" r="1.5" fill="white" opacity="0.4">
            <animate attributeName="opacity" values="0.1;0.4;0.1" dur="2s" repeatCount="indefinite" delay="0.5s" />
          </circle>
          <circle cx="65" cy="22" r="1.5" fill="white" opacity="0.4">
            <animate attributeName="opacity" values="0.1;0.4;0.1" dur="2s" repeatCount="indefinite" delay="1s" />
          </circle>
        </svg>
      </motion.div>

      {showText && (
        <div className="flex flex-col -space-y-1">
          <motion.h1 
            className={`${textClasses[size]} font-black tracking-tighter text-zinc-900 flex items-center`}
            initial={{ x: -10, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <span>Sent</span>
            <span className="text-green-600">lo</span>
          </motion.h1>
          {showSlogan && (
            <motion.p 
              className={`${sloganClasses[size]} font-black uppercase tracking-widest text-transparent bg-clip-text bg-linear-to-r from-green-600 via-emerald-500 to-green-700 whitespace-nowrap drop-shadow-sm`}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Sentlo it
            </motion.p>
          )}
        </div>
      )}
    </div>
  );
};
